import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { createContact } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import GdprConsent from "@/components/GdprConsent";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import BookingCalendar from "@/components/BookingCalendar";
import { trackLead } from "@/lib/pixel";
import { getUtmPayload } from "@/lib/utm";

const MOTIVI_RICHIESTA = [
  "Sto cercando personale da assumere",
  "Sto cercando un lavoro",
];

const DIMENSIONI_AZIENDA = ["<10", "10-25", "26-50", "50-100", "100+"];

// GHL "Ruolo" (SINGLE_OPTIONS) — value deve matchare esattamente lo slug del dropdown
const RUOLI_UTENTE = [
  { value: "ceo__founder", label: "CEO / Founder" },
  { value: "direttore_hr", label: "Direttore HR" },
  { value: "responsabile_di_funzione", label: "Responsabile di funzione" },
  { value: "coo", label: "COO" },
  { value: "operation_manager", label: "Operation manager" },
  { value: "altro", label: "Altro" },
  { value: "disoccupato", label: "Disoccupato" },
];

const EMPTY = {
  motivo_richiesta: "",
  nome: "",
  cognome: "",
  azienda: "",
  dimensione_azienda: "",
  ruolo: "",           // ruolo dell'utente (dropdown GHL "Ruolo")
  ruolo_cercato: "",   // figura che l'azienda sta cercando
  telefono: "",
  email: "",
};

export default function Contatti() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "motivo_richiesta", "nome", "cognome", "azienda",
      "dimensione_azienda", "ruolo", "ruolo_cercato", "telefono", "email",
    ];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Compila tutti i campi obbligatori");
        return;
      }
    }
    if (!gdpr) {
      toast.error("Devi accettare la Privacy Policy per procedere");
      return;
    }
    // Off-target = cerca lavoro / disoccupato → lead va comunque a GHL ma
    // NON scattiamo l'evento Meta Lead e mandiamo alla thank-you fuori target
    const isOffTarget =
      form.motivo_richiesta === "Sto cercando un lavoro" ||
      form.ruolo === "disoccupato";
    setLoading(true);
    try {
      await createContact({
        ...form,
        sorgente: isOffTarget ? "contatti-off-target" : "contatti",
        ...getUtmPayload(),
      });
      if (isOffTarget) {
        navigate("/lp/prenota-call/off-target", { replace: true });
        return;
      }
      trackLead({ content_name: "Contact form", content_category: "contatti" });
      setSent(true);
      toast.success("Richiesta inviata. Ora scegli lo slot in calendario.");
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contatti, Prenota una call con Lovac"
        description="Raccontaci chi stai cercando. Una call di 30 minuti con un recruiter Lovac. Se non siamo la soluzione giusta, te lo diciamo."
        path="/contatti"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: "https://lovac.it/contatti",
          name: "Contatti Lovac",
          inLanguage: "it-IT",
          breadcrumb: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Contatti", path: "/contatti" },
          ]),
        }}
      />
      {/* HERO */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-20 pb-16 relative z-10 max-w-3xl mx-auto text-center">
          <span className="badge-on-dark">Prenota una call</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 text-on-dark leading-[1.02]">
            Parliamoci.
          </h1>
          <p className="text-on-dark-muted text-lg mt-7 max-w-xl mx-auto leading-relaxed">
            Raccontaci chi stai cercando. Fissa una call in calendario con un consulente Lovac.
          </p>
        </div>
      </section>

      {/* FORM + STATS */}
      <section className="py-20 bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          {/* FORM */}
          <div className="card-lovac p-8 lg:p-10">
            {sent ? (
              <div className="py-6">
                <div className="text-center">
                  <CheckCircle2 size={46} className="text-lovac-accent mx-auto" data-testid="contact-success-icon" />
                  <h3 className="font-display text-3xl text-lovac-dark mt-6" data-testid="contact-success-title">
                    Un ultimo step per completare la tua richiesta.
                  </h3>
                  <p className="text-base text-lovac-muted mt-4 max-w-md mx-auto">
                    Scegli qui sotto uno slot in calendario per parlare con un consulente Lovac.
                  </p>
                </div>
                <div className="mt-8 -mx-2 sm:mx-0" data-testid="contact-booking-wrapper">
                  <BookingCalendar
                    id="booking-contatti"
                    prefill={{
                      first_name: form.nome,
                      last_name: form.cognome,
                      email: form.email,
                      phone: form.telefono,
                      company: form.azienda,
                    }}
                  />
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-nocapture="true" data-ghl-ignore="true" data-lc-ignore="true">
                <div>
                  <label className="text-xs uppercase tracking-wider text-lovac-muted">Motivo della richiesta <span className="text-lovac-accent">*</span></label>
                  <select
                    data-testid="contact-input-motivo"
                    className="input-lovac mt-2"
                    value={form.motivo_richiesta}
                    onChange={(e) => setForm({ ...form, motivo_richiesta: e.target.value })}
                  >
                    <option value="">Seleziona…</option>
                    {MOTIVI_RICHIESTA.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Nome <span className="text-lovac-accent">*</span></label>
                    <input data-testid={CONTACT.nome} className="input-lovac mt-2" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Cognome <span className="text-lovac-accent">*</span></label>
                    <input data-testid={CONTACT.cognome} className="input-lovac mt-2" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-lovac-muted">Azienda <span className="text-lovac-accent">*</span></label>
                  <input data-testid={CONTACT.azienda} className="input-lovac mt-2" value={form.azienda} onChange={(e) => setForm({ ...form, azienda: e.target.value })} />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Numero di dipendenti <span className="text-lovac-accent">*</span></label>
                    <select
                      data-testid="contact-input-dimensione"
                      className="input-lovac mt-2"
                      value={form.dimensione_azienda}
                      onChange={(e) => setForm({ ...form, dimensione_azienda: e.target.value })}
                    >
                      <option value="">Seleziona…</option>
                      {DIMENSIONI_AZIENDA.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Il tuo ruolo <span className="text-lovac-accent">*</span></label>
                    <select
                      data-testid={CONTACT.ruolo}
                      className="input-lovac mt-2"
                      value={form.ruolo}
                      onChange={(e) => setForm({ ...form, ruolo: e.target.value })}
                    >
                      <option value="">Seleziona…</option>
                      {RUOLI_UTENTE.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-lovac-muted">Ruolo cercato <span className="text-lovac-accent">*</span></label>
                  <input
                    data-testid="contact-input-ruolo-cercato"
                    className="input-lovac mt-2"
                    placeholder="es. Sales Manager / CFO / Lead Developer"
                    value={form.ruolo_cercato}
                    onChange={(e) => setForm({ ...form, ruolo_cercato: e.target.value })}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Telefono <span className="text-lovac-accent">*</span></label>
                    <input data-testid={CONTACT.telefono} className="input-lovac mt-2" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-lovac-muted">Email <span className="text-lovac-accent">*</span></label>
                    <input data-testid={CONTACT.email} type="email" className="input-lovac mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>

                <div className="pt-2">
                  <GdprConsent
                    checked={gdpr}
                    onChange={setGdpr}
                    variant="light"
                    testId="contact-gdpr"
                  />
                </div>

                <button data-testid={CONTACT.submit} disabled={loading} type="submit" className="btn-primary w-full mt-5">
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Invio…</>) : (<>Prenota una call <ArrowRight size={16} /></>)}
                </button>

                <p className="text-sm text-lovac-muted text-center pt-3 leading-relaxed">
                  <span className="text-lovac-dark font-medium">Compila il form.</span> Potrai poi scegliere uno slot in calendario per parlare con un consulente Lovac.
                </p>
              </form>
            )}
          </div>

          {/* STATS, credibility */}
          <aside className="lg:sticky lg:top-28">
            <h2 className="font-display text-3xl text-lovac-dark">Un esperto è a tua disposizione.</h2>
            <p className="text-base text-lovac-muted mt-4 max-w-md">
              Analizziamo la tua ricerca e in che modo possiamo chiuderla in tempi record. Senza impegno.
            </p>

            <div className="mt-10 space-y-5">
              <Stat n="+500" l="Figure reclutate" />
              <Stat n="+300" l="Aziende aiutate" />
              <Stat n="< 30 giorni" l="Time to hire medio" />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

const Stat = ({ n, l }) => (
  <div className="flex items-baseline gap-4">
    <p className="font-display text-4xl text-lovac-accent leading-none w-32 shrink-0">{n}</p>
    <p className="text-sm text-lovac-dark">{l}</p>
  </div>
);
